import { useState, useEffect } from 'react'
import { useTranslation }  from 'react-i18next'
import { Settings, User, Lock, BarChart2, Bot, Eye, EyeOff } from 'lucide-react'
import { PageHeader, Card, Input, Button, Toggle, FIELD_CLS } from '@/components/ui/primitives'
import { useTheme }        from '@/context/ThemeContext'
import { useAuth }         from '@/context/AuthContext'
import { updateProfile, changePassword, listGradingCriteria, getAISettings, patchAISettings, testAISettings } from '@/api/resources'
import { fetchAllPages } from '@/utils/fetchAll'

export default function SettingsPage() {
  const { t }           = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const { user }        = useAuth()

  const [notifications,  setNotifications]  = useState(true)
  const [emailAlerts,    setEmailAlerts]    = useState(false)
  const [fullName,       setFullName]       = useState(user?.full_name || '')
  const [profileSaving,  setProfileSaving]  = useState(false)
  const [profileMsg,     setProfileMsg]     = useState('')

  const [newPass, setNewPass]   = useState('')
  const [confPass, setConfPass] = useState('')
  const [passMsg,  setPassMsg]  = useState('')
  const [passSaving, setPassSaving] = useState(false)

  // Grading criteria default
  const [criteria,        setCriteria]        = useState([])
  const [defaultCriteria, setDefaultCriteria] = useState(user?.default_grading_criteria ?? '')
  const [criteriaSaving,  setCriteriaSaving]  = useState(false)
  const [criteriaMsg,     setCriteriaMsg]     = useState('')

  // AI provider settings (admin only)
  const isAdmin = user?.is_superuser || user?.role === 'admin'
  const [aiProvider,    setAiProvider]    = useState('gemini')
  const [aiApiKey,      setAiApiKey]      = useState('')
  const [aiModelName,   setAiModelName]   = useState('')
  const [aiKeyMasked,   setAiKeyMasked]   = useState('')
  const [aiDefaults,    setAiDefaults]    = useState({})
  const [showKey,       setShowKey]       = useState(false)
  const [aiSaving,      setAiSaving]      = useState(false)
  const [aiTesting,     setAiTesting]     = useState(false)
  const [aiMsg,         setAiMsg]         = useState('')
  const [aiTestResult,  setAiTestResult]  = useState(null)

  useEffect(() => {
    fetchAllPages(listGradingCriteria)
      .then(setCriteria)
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    getAISettings()
      .then(({ data }) => {
        setAiProvider(data.provider)
        setAiKeyMasked(data.api_key_masked || '')
        setAiModelName(data.model_name || '')
        setAiDefaults(data.default_model || {})
      })
      .catch(() => {})
  }, [isAdmin])

  async function handleTestAI() {
    setAiTesting(true); setAiTestResult(null)
    try {
      const { data } = await testAISettings()
      setAiTestResult({ ok: true, detail: data.detail })
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Ulanib bo\'lmadi'
      setAiTestResult({ ok: false, detail })
    } finally {
      setAiTesting(false)
    }
  }

  async function handleSaveAI() {
    setAiSaving(true); setAiMsg('')
    const payload = { provider: aiProvider, model_name: aiModelName }
    if (aiApiKey) payload.api_key = aiApiKey
    try {
      const { data } = await patchAISettings(payload)
      setAiKeyMasked(data.api_key_masked || '')
      setAiApiKey('')
      setShowKey(false)
      setAiMsg('Saqlandi!')
    } catch (e) {
      setAiMsg(e?.response?.data?.detail || 'Xatolik yuz berdi')
    } finally {
      setAiSaving(false)
      setTimeout(() => setAiMsg(''), 4000)
    }
  }

  async function handleSaveProfile() {
    setProfileSaving(true); setProfileMsg('')
    try {
      await updateProfile({ full_name: fullName })
      setProfileMsg('Saved!')
    } catch {
      setProfileMsg('Error saving profile')
    } finally {
      setProfileSaving(false)
      setTimeout(() => setProfileMsg(''), 3000)
    }
  }

  async function handleChangePassword() {
    if (newPass !== confPass) { setPassMsg('Passwords do not match'); return }
    setPassSaving(true); setPassMsg('')
    try {
      await changePassword({ new_password: newPass })
      setPassMsg('Password updated!'); setNewPass(''); setConfPass('')
    } catch (e) {
      setPassMsg(e?.response?.data?.detail || 'Error updating password')
    } finally {
      setPassSaving(false)
      setTimeout(() => setPassMsg(''), 4000)
    }
  }

  async function handleSaveDefaultCriteria() {
    setCriteriaSaving(true); setCriteriaMsg('')
    try {
      await updateProfile({ default_grading_criteria: defaultCriteria || null })
      setCriteriaMsg(t('common.save') + ' ✓')
    } catch (e) {
      setCriteriaMsg(e?.response?.data?.detail || 'Error')
    } finally {
      setCriteriaSaving(false)
      setTimeout(() => setCriteriaMsg(''), 3000)
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader
        title={t('nav.settings')}
        subtitle="Manage your account and preferences"
      />

      {/* Profile section */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <User size={18} className="text-blue-500" />
          <h2 className="font-semibold text-white">Profile</h2>
        </div>
        <div className="space-y-4">
          <Input
            label="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input label="Username" defaultValue={user?.username || ''} disabled />
          {profileMsg && <p className={`text-xs ${profileMsg.includes('Error') ? 'text-red-500' : 'text-emerald-600'}`}>{profileMsg}</p>}
          <div className="flex justify-end">
            <Button size="sm" loading={profileSaving} onClick={handleSaveProfile}>Save Profile</Button>
          </div>
        </div>
      </Card>

      {/* Password section */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <Lock size={18} className="text-amber-500" />
          <h2 className="font-semibold text-white">Change Password</h2>
        </div>
        <div className="space-y-4">
          <Input label="New Password"     type="password" placeholder="••••••••" value={newPass}  onChange={(e) => setNewPass(e.target.value)} />
          <Input label="Confirm Password" type="password" placeholder="••••••••" value={confPass} onChange={(e) => setConfPass(e.target.value)} />
          {passMsg && <p className={`text-xs ${passMsg.includes('Error') || passMsg.includes('match') ? 'text-red-500' : 'text-emerald-600'}`}>{passMsg}</p>}
          <div className="flex justify-end">
            <Button size="sm" loading={passSaving} onClick={handleChangePassword}>Update Password</Button>
          </div>
        </div>
      </Card>

      {/* Default Grading Criteria */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <BarChart2 size={18} className="text-indigo-500" />
          <div>
            <h2 className="font-semibold text-white">{t('grading.defaultCriteria', 'Standart baholash mezoni')}</h2>
            <p className="text-xs text-white/45 mt-0.5">{t('grading.defaultHint', 'Yangi imtihon yaratganda avtomatik tanlanadigan mezon')}</p>
          </div>
        </div>
        <div className="space-y-4">
          <select
            value={defaultCriteria}
            onChange={e => setDefaultCriteria(e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">{t('grading.noCriteria', 'Mezon tanlanmagan')}</option>
            {criteria.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {criteria.length === 0 && (
            <p className="text-xs text-gray-400">
              {t('grading.empty', 'Baholash mezonlari yo\'q')}. <a href="/grading-criteria" className="text-blue-600 hover:underline">{t('grading.createNew', 'Yangi mezon yarating')}</a>.
            </p>
          )}
          {/* Preview selected criteria thresholds */}
          {defaultCriteria && (() => {
            const selected = criteria.find(c => String(c.id) === String(defaultCriteria))
            if (!selected?.thresholds?.length) return null
            const sorted = [...selected.thresholds].sort((a, b) => b.min_percent - a.min_percent)
            return (
              <div className="bg-white/[0.04] rounded-xl p-3">
                <p className="text-xs font-medium text-white/45 mb-2">{t('grading.thresholds', 'Chegaralar')}:</p>
                <div className="flex flex-wrap gap-2">
                  {sorted.map((band, idx) => (
                    <div key={idx} className="text-xs bg-white/[0.06] border border-white/15 rounded-lg px-2 py-1">
                      <span className="font-medium text-white">{band.label}</span>
                      <span className="text-gray-400 ml-1">({band.min_percent}–{band.max_percent}%)</span>
                      {band.grade_point && <span className="text-blue-600 ml-1 font-semibold">{band.grade_point}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}
          {criteriaMsg && <p className={`text-xs ${criteriaMsg.includes('Error') ? 'text-red-500' : 'text-emerald-600'}`}>{criteriaMsg}</p>}
          <div className="flex justify-end">
            <Button size="sm" loading={criteriaSaving} onClick={handleSaveDefaultCriteria}>{t('common.save')}</Button>
          </div>
        </div>
      </Card>

      {/* AI Provider Settings — admin only */}
      {isAdmin && (
        <Card>
          <div className="flex items-center gap-3 mb-5">
            <Bot size={18} className="text-emerald-500" />
            <div>
              <h2 className="font-semibold text-white">AI Tekshirish Sozlamalari</h2>
              <p className="text-xs text-white/45 mt-0.5">Ochiq savollarni baholash uchun AI provayderni sozlang</p>
            </div>
          </div>
          <div className="space-y-4">
            {/* Provider selector */}
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Provayderr</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'gemini', label: 'Google Gemini', color: 'text-blue-400' },
                  { value: 'openai', label: 'OpenAI ChatGPT', color: 'text-emerald-400' },
                  { value: 'claude', label: 'Anthropic Claude', color: 'text-orange-400' },
                ].map(p => (
                  <button
                    key={p.value}
                    onClick={() => setAiProvider(p.value)}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${
                      aiProvider === p.value
                        ? 'border-white/30 bg-white/10 text-white'
                        : 'border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/[0.06]'
                    }`}
                  >
                    <span className={p.color}>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">API Kalit</label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={aiApiKey}
                  onChange={e => setAiApiKey(e.target.value)}
                  placeholder={aiKeyMasked ? aiKeyMasked : 'Yangi kalit kiriting...'}
                  className={`${FIELD_CLS} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowKey(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {aiKeyMasked && !aiApiKey && (
                <p className="text-xs text-white/35 mt-1">Joriy kalit: {aiKeyMasked} — o'zgartirish uchun yangi kalit kiriting</p>
              )}
            </div>

            {/* Model name */}
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">
                Model nomi <span className="text-white/30 font-normal">(ixtiyoriy)</span>
              </label>
              <input
                type="text"
                value={aiModelName}
                onChange={e => setAiModelName(e.target.value)}
                placeholder={aiDefaults[aiProvider] ? `Standart: ${aiDefaults[aiProvider]}` : 'Masalan: gpt-4o'}
                className={FIELD_CLS}
              />
            </div>

            {aiTestResult && (
              <div className={`rounded-xl px-3 py-2.5 text-xs flex items-start gap-2 ${
                aiTestResult.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                <span className="text-base leading-none">{aiTestResult.ok ? '✓' : '✕'}</span>
                <span>{aiTestResult.detail}</span>
              </div>
            )}
            {aiMsg && (
              <p className={`text-xs ${aiMsg.includes('atolik') ? 'text-red-400' : 'text-emerald-400'}`}>{aiMsg}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                loading={aiTesting}
                onClick={handleTestAI}
              >
                Ulanishni tekshirish
              </Button>
              <Button size="sm" loading={aiSaving} onClick={handleSaveAI}>Saqlash</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Preferences */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <Settings size={18} className="text-purple-500" />
          <h2 className="font-semibold text-white">Preferences</h2>
        </div>
        <div className="space-y-5">
          <Toggle
            label="Dark Mode"
            hint="Switch between light and dark interface"
            checked={theme === 'dark'}
            onChange={toggleTheme}
          />
          <Toggle
            label="Push Notifications"
            hint="Receive notifications about new exams and results"
            checked={notifications}
            onChange={setNotifications}
          />
          <Toggle
            label="Email Alerts"
            hint="Get email summaries of student activity"
            checked={emailAlerts}
            onChange={setEmailAlerts}
          />
        </div>
      </Card>
    </div>
  )
}
